'use client'

import Link from 'next/link'
import { useRouter } from 'next/navigation'
import React, { useEffect, useState } from 'react'
import ProductCart from '@/components/Cart/ProductCart'
import GSwal from '@/components/GSwal'
import Modal from '@/components/Layout/Modal'
import useGa4DataLayer from '@/hooks/business/analytics/useGa4DataLayer'
import { useIsLoggedIn } from '@/hooks/business/auth/useIsLoggedIn'
import { useApplyCouponFromUrl, useCart, useCartShipping, useOutOfStock, useRemoveCoupon } from '@/hooks/business/cart'
import { usePreferredZipCode } from '@/hooks/business/customer/usePreferredZipCode'
import { useHasMounted } from '@/hooks/ui/useHasMounted'
import ApplyCouponModal from './_components/ApplyCouponModal'
import CartRecommendations from './_components/CartRecommendations'
import CartSummary from './_components/CartSummary'
import ShippingForm from './_components/ShippingForm'
import ShippingQuotes from './_components/ShippingQuotes'
import TempUserLogin from './_components/TempUserLogin'

/**
 * Carrinho. O carrinho vem da `useCartStore` (revalidada pelo `cart/current`), o frete é recalculado ao abrir quando o
 * carrinho já tem CEP (com frete por vendedor, o cliente escolhe a opção de cada um), e o cupom da URL é aplicado ao
 * abrir. Sem login, "Finalizar Pedido" pede o e-mail.
 */
const Carrinho: React.FC = () => {
    const hasMounted = useHasMounted()
    const router = useRouter()
    const { ga4Event } = useGa4DataLayer()
    const { cart, items, isEmpty } = useCart()
    const shipping = useCartShipping({ autoCalculate: true })
    // Carrinho sem CEP: o campo começa com o CEP do último frete do visitante (ou do endereço principal), sem calcular.
    const preferredZipCode = usePreferredZipCode()
    const { canCheckout, outOfStockMessage } = useOutOfStock()
    const { removeCoupon } = useRemoveCoupon()
    const isLoggedIn = useIsLoggedIn()
    const [isEmailModalOpen, setIsEmailModalOpen] = useState(false)
    const [isCouponModalOpen, setIsCouponModalOpen] = useState(false)

    useApplyCouponFromUrl()

    // biome-ignore lint/correctness/useExhaustiveDependencies: o evento do GA4 sai a cada carrinho novo, como antes (o ga4Event muda a cada render)
    useEffect(() => {
        if (cart && cart.cartItems.length > 0) ga4Event.viewCart(cart)
    }, [cart])

    const goToCheckout = () => {
        if (!isLoggedIn) {
            setIsEmailModalOpen(true)
            return
        }
        if (canCheckout) {
            router.push('/checkout')
            return
        }
        GSwal.fire({ icon: 'error', title: 'Oops...', text: outOfStockMessage })
    }

    // A store persistida só existe no navegador: antes de montar, nada (sem diferença de hidratação).
    if (!hasMounted) return null

    if (!cart || isEmpty) {
        return (
            <div className="flex flex-col items-center justify-center h-full p-24 mb-10 mt-14">
                <h1 className="mb-2 text-xl font-bold text-primary font-sora">Ainda não há itens no seu carrinho</h1>
                <Link href="/produtos" className="mt-2 uppercase text-orange">
                    Continuar Comprando
                </Link>
            </div>
        )
    }

    return (
        <div className="">
            <div className="grid grid-cols-3 max-lg:flex max-lg:flex-col w-full px-[100px] max-md:px-4 mt-[50px] mb-[200px]">
                <div className="col-span-2 pr-4 *:not-first:mt-4 lg:border-r lg:border-submenu-gray ">
                    {/* A API não manda id do item: a chave é o produto (um item por produto). */}
                    {items.map((item) => (
                        <ProductCart key={item.product.id} data={item} quantity={item.quantity} />
                    ))}
                    <ShippingQuotes
                        quotes={shipping.quotes}
                        shippingTax={shipping.shippingTax}
                        hasZipCode={Boolean(cart.cep)}
                        quoteFailed={shipping.hasQuoteFailure}
                        sellers={shipping.sellers}
                        pendingSelection={shipping.pendingSelection}
                        onSelectShipping={(sellerId, offerCode) => void shipping.selectShipping(sellerId, offerCode)}
                    />
                    <CartRecommendations items={items} />
                </div>
                <div className="-mt-6 *:not-first:mt-4 md:pl-4 max-md:mt-2">
                    <ShippingForm
                        zipCode={cart.cep || preferredZipCode}
                        lastValidZipCode={cart.cep ?? ''}
                        onCalculate={shipping.calculate}
                        error={shipping.calculationError}
                    />
                    <CartSummary
                        cart={cart}
                        canCheckout={canCheckout}
                        isShippingPending={shipping.isSelecting}
                        shippingFailed={shipping.hasQuoteFailure}
                        onOpenCoupon={() => setIsCouponModalOpen(true)}
                        onRemoveCoupon={() => removeCoupon().catch(() => {})}
                        onCheckout={goToCheckout}
                    />
                </div>
            </div>
            <Modal
                headerText="Você não está logado."
                isOpen={isEmailModalOpen}
                onClose={() => setIsEmailModalOpen(false)}
                className="w-2/5"
                closeBtn={false}
            >
                <TempUserLogin onClose={() => setIsEmailModalOpen(false)} />
            </Modal>
            <ApplyCouponModal isCouponModalOpen={isCouponModalOpen} setIsCouponModalOpen={setIsCouponModalOpen} />
        </div>
    )
}

export default Carrinho
